import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, isSupabaseConfigured } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, metadata: { full_name: string; phone: string }) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      isLoading: true,
      isAuthenticated: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setSession: (session) => set({ session }),

      setProfile: (profile) => set({ profile }),

      setLoading: (isLoading) => set({ isLoading }),

      signIn: async (email, password) => {
        set({ isLoading: true });

        if (!isSupabaseConfigured) {
          set({ isLoading: false });
          return {
            success: false,
            error: 'Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file.'
          };
        }

        try {
          const { data, error } = await auth.signIn(email, password);

          console.log('Sign in response:', { data, error });

          if (error) {
            console.error('Sign in error:', error);
            throw error;
          }

          if (data.user && data.session) {
            console.log('Sign in successful:', data.user.email);
            set({
              user: data.user,
              session: data.session,
              isAuthenticated: true,
              isLoading: false,
            });

            return { success: true };
          }

          throw new Error('Sign in failed - no user or session returned');
        } catch (error: any) {
          console.error('Sign in catch error:', error);
          set({ isLoading: false });
          return { success: false, error: error.message };
        }
      },

      signUp: async (email, password, metadata) => {
        set({ isLoading: true });

        if (!isSupabaseConfigured) {
          set({ isLoading: false });
          return {
            success: false,
            error: 'Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file.'
          };
        }

        try {
          const { data, error } = await auth.signUp(email, password, metadata);

          console.log('Sign up response:', { data, error });

          if (error) {
            console.error('Sign up error:', error);
            throw error;
          }

          if (data.user) {
            console.log('Sign up successful:', data.user.email, 'Session:', !!data.session);

            set({
              user: data.user,
              session: data.session,
              isAuthenticated: !!data.session,
              isLoading: false,
            });

            // Create profile in profiles table
            const { default: supabase } = await import('../lib/supabase');
            await supabase
              .from('profiles')
              .upsert({
                user_id: data.user.id,
                full_name: metadata.full_name,
                phone: metadata.phone,
              });

            return { success: true };
          }

          throw new Error('Sign up failed - no user returned');
        } catch (error: any) {
          console.error('Sign up catch error:', error);
          set({ isLoading: false });
          return { success: false, error: error.message };
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        try {
          await auth.signOut();
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          console.error('Sign out error:', error);
          set({ isLoading: false });
        }
      },

      resetPassword: async (email) => {
        try {
          const { error } = await auth.resetPassword(email);

          if (error) throw error;

          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },

      initialize: async () => {
        set({ isLoading: true });

        if (!isSupabaseConfigured) {
          set({ isLoading: false });
          return;
        }

        try {
          const { data } = await auth.getSession();

          if (data.session) {
            set({
              session: data.session,
              user: data.session.user,
              isAuthenticated: true,
            });

            // Fetch profile
            // In real implementation, this would fetch from Supabase
          }
        } catch (error) {
          console.error('Initialize auth error:', error);
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        profile: state.profile,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth on app load
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize();

  // Listen to auth changes only if Supabase is configured
  if (isSupabaseConfigured) {
    auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        useAuthStore.getState().setSession(session);
        useAuthStore.getState().setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        useAuthStore.getState().setUser(null);
        useAuthStore.getState().setSession(null);
        useAuthStore.getState().setProfile(null);
      }
    });
  }
}
